import React, { useMemo, memo } from 'react';
import { connect } from 'react-redux';
import { Grid, Box, Typography, CircularProgress } from '@mui/material';
import { withStyles } from 'tss-react/mui';

const styles = theme => ({
    activityOverlay: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1,
    },
    loadingSpinner: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1
    },
    activityText: {
        textShadow: '0px 0px 5px #000000',
        position: 'absolute',
        top: 'calc(50% - 70px)',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1
    },
    saveProgress: {
        zIndex: 1,
        width: '100%',
        position: 'absolute',
        top: 0
    },
    saveProgressText: {
        textShadow: '0px 0px 5px #000000',
        zIndex: 1,
        position: 'absolute',
        top: theme.spacing(2),
        left: theme.spacing(2)
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1,
    },
    displayLoaderWrapper: {
        position: 'relative',
        width: 450,
        height: 80,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }
});

export const DisplayLoaderPercentageId = 'display-loader-percentage';

// Updates the display loader percentage without react lifecycle.
// Percentage update is frequently called that causes the component to re-render.
// This function updates the DOM directly to improve performance and save CPU and memory.
export const updateDisplayLoaderPercentage = (percentage) => {
    if (window.displayLoaderPercentageElement && !percentage) {
        window.displayLoaderPercentageElement.innerHTML = '';
        window.displayLoaderPercentageElement = null;
        return;
    }

    if (!window.displayLoaderPercentageElement) {
        window.displayLoaderPercentageElement = document.getElementById(DisplayLoaderPercentageId);
    }
    
    if (!window.displayLoaderPercentageElement)
        return;

    window.displayLoaderPercentageElement.innerText = `${percentage}%`;
}

// Memoized loader components
const DefaultLoader = memo(({ classes, message }) => (
    <Box className={classes.activityOverlay}>
        <Grid
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            style={{ height: '100%', position: 'relative' }}
            spacing={2}
        >
            <Box className={classes.displayLoaderWrapper}>
                <CircularProgress size={80} />
                <Typography
                    variant='h5'
                    className={classes.activityText}
                >
                    {message}
                    <span id={DisplayLoaderPercentageId}></span>
                </Typography>
            </Box>
        </Grid>
    </Box>
));

const VideoSettingsLoader = memo(({ classes }) => (
    <Box className={classes.loadingOverlay}>
        <Grid
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            style={{ height: '100%', maxWidth: '100%' }}
            spacing={2}
        >
            <Grid>
                <Typography variant='h5'>
                    Loading video settings...
                </Typography>
            </Grid>
            <Grid style={{ width: 450 }}>
                <CircularProgress size={80} />
            </Grid>
        </Grid>
    </Box>
));

const SpinnerLoader = memo(({ classes }) => (
    <Box className={classes.loadingSpinner}>
        <CircularProgress size={80} />
    </Box>
));

function PreviewDisplayLoaders(props) {
    const {
        classes, audioAnalyzing, audioLoading, audioWaiting, backgroundVideosLoading,
        audioPreparing, videoSettingsLoading, webCodecsBufferLoading, backgroundMediaResourceType,
        backgroundUploading, imageElementUploading
    } = props;

    // Use useMemo to only recreate the component when dependencies change
    return useMemo(() => {
        if (audioWaiting) {
            return <SpinnerLoader classes={classes} />;
        }

        if (videoSettingsLoading) {
            return <VideoSettingsLoader classes={classes} />;
        }

        if (audioAnalyzing) {
            return <DefaultLoader classes={classes} message="Analyzing audio..." />;
        }

        if (audioLoading || audioPreparing) {
            return <DefaultLoader classes={classes} message="Loading audio data..." />;
        }

        // The direct background video upload reports its percentage into this loader's span,
        // the same way the direct audio upload does through the audioPreparing loader.
        if (backgroundUploading) {
            return <DefaultLoader classes={classes} message="Uploading background..." />;
        }

        if (imageElementUploading) {
            return <DefaultLoader classes={classes} message="Uploading image..." />;
        }

        const isVideoBackground = backgroundMediaResourceType === 1;
        const isLoadingVideo = isVideoBackground && (backgroundVideosLoading || webCodecsBufferLoading);
        
        if (isLoadingVideo) {
            return <DefaultLoader classes={classes} message="Analyzing background..." />;
        }

        return null;
    }, [
        classes, 
        audioAnalyzing, 
        audioLoading, 
        audioWaiting, 
        backgroundVideosLoading, 
        audioPreparing, 
        videoSettingsLoading,
        webCodecsBufferLoading,
        backgroundMediaResourceType,
        backgroundUploading,
        imageElementUploading
    ]);
}

const mapStateToProps = (state) => {
    const {
        audioPreparing,
        audioAnalyzing,
        audioLoading,
        audioWaiting,
        backgroundUploading,
        imageElementUploading,
        backgroundVideosLoading,
        videoSettingsLoading,
        webCodecsBufferLoading
    } = state.create;

    const backgroundMediaSource = state.createPreview.present.videoSettings?.background?.mediaSource;
    const backgroundMediaResourceType = backgroundMediaSource?.mediaResourceType;

    return {
        audioPreparing,
        audioAnalyzing,
        audioLoading,
        audioWaiting,
        videoSettingsLoading,
        backgroundUploading,
        imageElementUploading,
        backgroundVideosLoading,
        webCodecsBufferLoading,
        backgroundMediaResourceType
    }
};

export default connect(mapStateToProps)(memo(withStyles(PreviewDisplayLoaders, styles)));